import moment from 'moment';
import { useEffect, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '../../components-v2/Popover';
import { CalendarIcon } from '../../components/svgs/icons/CalendarIcon';
import { Button } from '../Button';
import { ButtonGroup } from '../ButtonGroup';
import { Calendar } from '../Calendar';
import { Input } from '../Input';
import { css } from 'aphrodite';
import { styleSheet } from './styles';

type Props = {
	value: string;
	onChange: (value: string) => void;
	disabled?: boolean;
	/**
	 * `YYYY-MM-DD`. Advisory only: earlier days stay selectable, for a date that can be justified
	 * rather than forbidden. Shown in the footer via `minimumDateLabel`.
	 */
	minimumDate?: string;
	/** Footer wording for whichever minimum applies. Defaults to naming it as the earliest available date. */
	minimumDateLabel?: (formattedDate: string) => string;
	/**
	 * `YYYY-MM-DD`. Enforced: earlier days cannot be selected, and the popover opens on this month
	 * when there is no value. Takes precedence over `minimumDate` where both are given.
	 */
	earliestSelectableDate?: string;
	/** `YYYY-MM-DD`. Enforced: later days cannot be selected. */
	latestSelectableDate?: string;
	/** Footer wording for `latestSelectableDate`. Defaults to naming it as the latest available date. */
	latestSelectableDateLabel?: (formattedDate: string) => string;
	/**
	 * `Date.getDay()` numbers (0 = Sunday) that cannot be picked, applied on top of any date floor —
	 * for an answer that has to land on a day the account can actually act on.
	 */
	unavailableDaysOfWeek?: number[];
};

/**
 * The days that can still be chosen but cost a written justification: after today, and before the
 * advisory minimum — which is the first day that needs no reason at all.
 *
 * Bucketed by position within the window rather than by fixed offsets from today. The window is
 * computed per firm and per request, so its length is not knowable here: keying off "+1, +2, +3"
 * would under-mark a longer window and mark an acceptable day in a shorter one.
 */
const shortNoticeBuckets = (
	minimumDate: string | undefined,
	latestDate: string | undefined,
	bucketCount = 3
): Date[][] => {
	const buckets: Date[][] = Array.from({ length: bucketCount }, (): Date[] => []);
	const minimum = minimumDate ? moment(minimumDate, 'YYYY-MM-DD', true) : null;
	if (!minimum?.isValid()) {
		return buckets;
	}
	const days: Date[] = [];
	const day = moment().startOf('day').add(1, 'day');
	const latest = latestDate ? moment(latestDate, 'YYYY-MM-DD', true) : null;
	while (day.isBefore(minimum, 'day') && !(latest?.isValid() && day.isAfter(latest, 'day'))) {
		days.push(day.toDate());
		day.add(1, 'day');
	}
	days.forEach((date, index) => {
		buckets[Math.min(bucketCount - 1, Math.floor((index * bucketCount) / days.length))].push(date);
	});
	return buckets;
};

// Common date formats to try parsing
// Strict M and D tokens reject a leading zero, so partly padded input like 09/5 needs its own formats.
const DATE_FORMATS = [
	'YYYY-MM-DD',
	'MM/DD/YYYY',
	'M/D/YYYY',
	'MM/D/YYYY',
	'M/DD/YYYY',
	'MM-DD-YYYY',
	'M-D-YYYY',
	'MM-D-YYYY',
	'M-DD-YYYY',
	'MM/DD/YY',
	'M/D/YY',
	'MM/D/YY',
	'M/DD/YY',
	'MM-DD-YY',
	'M-D-YY',
	'MM-D-YY',
	'M-DD-YY',
	'YYYY/MM/DD',
];

// A full ISO datetime (the shape the API round-trips) normalizes down to the YYYY-MM-DD the field edits.
const normalizeExternalValue = (raw: string) => {
	const parsed = moment(raw, moment.ISO_8601, true);
	return parsed.isValid() ? parsed.format('YYYY-MM-DD') : raw;
};

export const DateInput = ({
	value,
	onChange,
	disabled,
	minimumDate,
	minimumDateLabel,
	earliestSelectableDate,
	latestSelectableDate,
	latestSelectableDateLabel,
	unavailableDaysOfWeek,
}: Props) => {
	const [localValue, setLocalValue] = useState(() => normalizeExternalValue(value));
	const [isCalendarOpen, setIsCalendarOpen] = useState(false);
	const placeholder = 'YYYY-MM-DD';

	// Sync local state with prop when prop changes (e.g., after API response)
	useEffect(() => {
		setLocalValue(normalizeExternalValue(value));
	}, [value]);

	const footerDate = earliestSelectableDate ?? minimumDate;

	// Only where the minimum is advisory. An enforced floor already greys those days out, so tinting
	// them would decorate something the advisor cannot act on.
	const [shortNoticeStrong, shortNoticeMid, shortNoticeSoft] = shortNoticeBuckets(
		earliestSelectableDate ? undefined : minimumDate,
		latestSelectableDate
	);

	// An advisory minimum is what the firm asks for, not what it accepts — an earlier date still files
	// with a reason — so only the past is unavailable. An enforced one has no such escape, so it really
	// is the earliest selectable day. Unavailable weekdays stack on top of whichever floor applies.
	const disabledDays = [
		...(earliestSelectableDate
			? [{ before: moment(earliestSelectableDate, 'YYYY-MM-DD').toDate() }]
			: minimumDate
				? [{ before: moment().startOf('day').toDate() }]
				: []),
		...(latestSelectableDate ? [{ after: moment(latestSelectableDate, 'YYYY-MM-DD').toDate() }] : []),
		...(unavailableDaysOfWeek?.length ? [{ dayOfWeek: unavailableDaysOfWeek }] : []),
	];

	const handleCalendarSelect = (newValue: string) => {
		setLocalValue(newValue); // Update immediately for responsiveness
		onChange(newValue); // Trigger API call immediately for calendar selection
		setIsCalendarOpen(false); // Close the calendar after selection
	};

	const handleInputChange = (newValue: string) => {
		setLocalValue(newValue); // Update display immediately, but don't trigger API yet
	};

	const handleInputBlur = () => {
		// Only trigger onChange when user is done typing and value has changed
		if (localValue === normalizeExternalValue(value)) {
			return;
		}

		// If empty, pass through as-is
		if (!localValue.trim()) {
			onChange(localValue);
			return;
		}

		// Try to parse with various formats and normalize to YYYY-MM-DD
		const parsed = moment(localValue, DATE_FORMATS, true);
		if (parsed.isValid()) {
			const normalized = parsed.format('YYYY-MM-DD');
			setLocalValue(normalized); // Update display to normalized format
			onChange(normalized);
		} else {
			// Pass through as-is and let the handler decide what to do
			onChange(localValue);
		}
	};

	return (
		<ButtonGroup>
			<Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
				<PopoverTrigger asChild>
					<Button variant='secondary' disabled={disabled}>
						<CalendarIcon />
					</Button>
				</PopoverTrigger>
				<PopoverContent side='bottom'>
					<Calendar
						// An enforced floor in the future puts today inside the disabled range, where the
						// default grey fill lands under the fade and reads as neither today nor selectable.
						classNames={
							earliestSelectableDate && moment(earliestSelectableDate, 'YYYY-MM-DD').isAfter(moment(), 'day')
								? { today: css(styleSheet.todayOutlined) }
								: undefined
						}
						disabled={disabledDays.length ? disabledDays : undefined}
						modifiers={{
							shortNoticeMid,
							shortNoticeSoft,
							shortNoticeStrong,
						}}
						modifiersClassNames={{
							shortNoticeMid: css(styleSheet.shortNoticeMid),
							shortNoticeSoft: css(styleSheet.shortNoticeSoft),
							shortNoticeStrong: css(styleSheet.shortNoticeStrong),
						}}
						footer={
							footerDate || latestSelectableDate ? (
								<>
									{footerDate ? (
										<span className={css(styleSheet.footer)}>
											{(minimumDateLabel ?? (d => `Earliest available: ${d}`))(
												moment(footerDate, 'YYYY-MM-DD').format('MMM D, YYYY')
											)}
										</span>
									) : null}
									{latestSelectableDate ? (
										<span className={css(styleSheet.footer)}>
											{(latestSelectableDateLabel ?? (d => `Latest available: ${d}`))(
												moment(latestSelectableDate, 'YYYY-MM-DD').format('MMM D, YYYY')
											)}
										</span>
									) : null}
								</>
							) : undefined
						}
						mode='single'
						selected={localValue && moment(localValue).isValid() ? moment(localValue).toDate() : undefined}
						// Opening on today would land an unanswered question in a month that is entirely
						// unavailable whenever the floor is further out than that.
						defaultMonth={
							localValue && moment(localValue).isValid()
								? moment(localValue).toDate()
								: earliestSelectableDate
									? moment(earliestSelectableDate, 'YYYY-MM-DD').toDate()
									: new Date()
						}
						onSelect={day => {
							// Re-clicking the selected day deselects, handing back undefined — and
							// moment(undefined) is now, which would quietly write today past any floor.
							if (!day) {
								return;
							}
							handleCalendarSelect(moment(day).format('YYYY-MM-DD'));
						}}
					/>
				</PopoverContent>
			</Popover>
			<Input
				value={localValue}
				onChange={e => handleInputChange(e.target.value)}
				onBlur={handleInputBlur}
				placeholder={placeholder}
				disabled={disabled}
			/>
		</ButtonGroup>
	);
};
